import { createDatabase } from './CreateDatabase'; // Import the createDatabase function

// Insert or update universities data in IndexedDB
const insertUniversityToDB = async (universities) => {
    const db = await createDatabase(); // Ensure the database is set up
    const tx = db.transaction('universities', 'readwrite');
    const store = tx.objectStore('universities');

    try {
        // Insert each university and log confirmation
        for (const university of universities) {
            await store.put(university); // Use `put` to add or update university entries
        }

        await tx.done; // Ensure transaction completion
       //  console.log("All universities inserted/updated successfully.");
    } catch (error) {
        console.error('Error inserting or updating universities:', error);
        throw error;
    }
};

// Retrieve all universities from IndexedDB and sort by name
const getUniversitiesFromDB = async () => {
    const db = await createDatabase(); // Ensure the database is set up
    const tx = db.transaction('universities', 'readonly');
    const store = tx.objectStore('universities');

    try {
        const universities = await store.getAll(); // Get all records in the store
        await tx.done; // Ensure transaction completion
        universities.sort((a, b) => a.name.localeCompare(b.name)); // Sort by name
        return universities;
    } catch (error) {
        console.error("An error occurred while retrieving universities:", error);
        throw error;
    }
};

const getUniversitiesFromDBStatusActif = async () => {
    const db = await createDatabase(); // Ensure the database is set up
    const tx = db.transaction('universities', 'readonly');
    const store = tx.objectStore('universities');

    try {
        const universities = await store.getAll(); // Get all records in the store
        await tx.done; // Ensure transaction completion

        // Filter universities with status === "1" (as it might be a string)
        const filteredUniversities = universities
            .filter((university) => String(university.status) === "1") // Ensure correct type comparison
            .sort((a, b) => a.name.localeCompare(b.name)); // Sort by name

        return filteredUniversities;
    } catch (error) {
        console.error("An error occurred while retrieving universities:", error);
        throw error;
    }
};

// Count all universities in IndexedDB
const countUniversitiesFromDB = async () => {
    const db = await createDatabase(); // Ensure the database is set up
    const tx = db.transaction('universities', 'readonly');
    const store = tx.objectStore('universities');

    try {
        const count = await store.count(); // Count records in the object store
        await tx.done; // Ensure transaction completion
      //   console.log("Count of universities in IndexedDB:", count);
        return count;
    } catch (error) {
        console.error('Error counting universities:', error);
        throw error;
    }
};

// Delete a university from IndexedDB by ID
const deleteUniversityFromDB = async (universityID) => {
    const db = await createDatabase(); // Ensure the database is set up
    const tx = db.transaction('universities', 'readwrite');
    const store = tx.objectStore('universities');

    try {
        await store.delete(universityID); // Delete university by ID
        await tx.done; // Ensure transaction completion
     //    console.log(`University with ID ${universityID} deleted successfully.`);
    } catch (error) {
        console.error(`Error deleting university with ID ${universityID}:`, error);
        throw error;
    }
};

// Clear all universities data from IndexedDB
const clearUniversitiesFromDB = async () => {
    const db = await createDatabase(); // Ensure the database is set up
    const tx = db.transaction('universities', 'readwrite');
    const store = tx.objectStore('universities');

    try {
        await store.clear(); // Clear all data from the object store
        await tx.done; // Ensure transaction completion
     //    console.log("All universities cleared from IndexedDB.");
    } catch (error) {
        console.error('Error clearing universities from IndexedDB:', error);
        throw error;
    }
};

// Fetch both universities and the count of universities in parallel
const fetchUniversitiesAndCount = async (universitiesFromAPI) => {
    try {
        // Clear old universities before inserting new ones
        await clearUniversitiesFromDB();

        // Insert new universities
        await insertUniversityToDB(universitiesFromAPI);

        // Retrieve updated data and count
        const [universities, count] = await Promise.all([
            getUniversitiesFromDB(), // Retrieves sorted universities
            countUniversitiesFromDB(), // Retrieves the total count
            getUniversitiesFromDBStatusActif()
        ]);

        return { universities, count };
    } catch (error) {
        console.error('Error fetching universities and count:', error);
        throw error;
    }
};

// Clear database upon logout
const clearDatabaseOnLogout = async () => {
    try {
        await clearUniversitiesFromDB(); // Clear universities data
     //    console.log("IndexedDB cleared successfully on logout.");
    } catch (error) {
        console.error("Error clearing database on logout:", error);
    }
};

// Example: Delete a university both in backend and IndexedDB
const deleteUniversity = async (universityID) => {
    try {
        // Delete university in the backend
        await fetch(`/api/universities/${universityID}`, { method: 'DELETE' });

        // Delete university from IndexedDB
        await deleteUniversityFromDB(universityID);

      //   console.log(`University with ID ${universityID} deleted successfully.`);
    } catch (error) {
        console.error(`Failed to delete university with ID ${universityID}:`, error);
        throw error;
    }
};

export {
    insertUniversityToDB,
    getUniversitiesFromDB,
    countUniversitiesFromDB,
    deleteUniversityFromDB,
    clearUniversitiesFromDB,
    fetchUniversitiesAndCount,
    clearDatabaseOnLogout,
    deleteUniversity,
    getUniversitiesFromDBStatusActif
};